import { supabase } from './supabaseClient';

export interface PaymentDeletionAuditRow {
  id: string;
  deleted_at: string;
  payment_id: string;
  order_id: string | null;
  order_number: string | null;
  client_name: string | null;
  amount: number;
  payment_date: string | null;
  payment_method: string | null;
  payment_created_at: string | null;
  deleted_by_user_id: string | null;
  deleted_by_auth_user_id: string | null;
  deleted_by_email: string | null;
  deleted_by_full_name: string | null;
  deleted_by_role: string | null;
  store_id: string | null;
  store_name: string | null;
}

export interface PaymentHistoryEntry {
  type: 'added' | 'deleted';
  timestamp: string;
  amount: number;
  payment_date: string | null;
  payment_method: string | null;
  actor_email?: string | null;
  actor_full_name?: string | null;
  actor_role?: string | null;
}

export const paymentDeletionAuditService = {
  async getAll(): Promise<PaymentDeletionAuditRow[]> {
    const { data, error } = await supabase
      .from('payment_deletion_audit')
      .select('*')
      .order('deleted_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async getSince(timestamp: string): Promise<PaymentDeletionAuditRow[]> {
    const { data, error } = await supabase
      .from('payment_deletion_audit')
      .select('*')
      .gt('deleted_at', timestamp)
      .order('deleted_at', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  async getDeletionsForOrder(orderId: string): Promise<PaymentDeletionAuditRow[]> {
    const { data, error } = await supabase
      .from('payment_deletion_audit')
      .select('*')
      .eq('order_id', orderId)
      .order('deleted_at', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  async getPaymentHistory(orderId: string): Promise<PaymentHistoryEntry[]> {
    const [paymentsResult, auditResult] = await Promise.all([
      supabase
        .from('payments')
        .select('amount, payment_date, payment_method, created_at')
        .eq('order_id', orderId),
      this.getDeletionsForOrder(orderId),
    ]);

    if (paymentsResult.error) throw paymentsResult.error;

    const added: PaymentHistoryEntry[] = (paymentsResult.data || []).map(p => ({
      type: 'added',
      timestamp: p.created_at || p.payment_date || '',
      amount: Number(p.amount) || 0,
      payment_date: p.payment_date,
      payment_method: p.payment_method,
    }));

    const deleted: PaymentHistoryEntry[] = auditResult.map(a => ({
      type: 'deleted',
      timestamp: a.deleted_at,
      amount: Number(a.amount) || 0,
      payment_date: a.payment_date,
      payment_method: a.payment_method,
      actor_email: a.deleted_by_email,
      actor_full_name: a.deleted_by_full_name,
      actor_role: a.deleted_by_role,
    }));

    return [...added, ...deleted].sort((a, b) => a.timestamp.localeCompare(b.timestamp));
  },
};
